import { Card, CardContent, Badge, Button } from "@tellescope/ui";
import { formatDate, formatCurrency } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { FileText, Download, ExternalLink } from "lucide-react";
import { i18n } from "@tellescope/i18n";
import type { StripeInvoice } from "../../../hooks/useStripeInvoices";

interface InvoiceCardProps {
  invoice: StripeInvoice;
}

const getStatusInfo = (
  status: StripeInvoice["status"]
): {
  badgeVariant: "default" | "secondary" | "destructive" | "outline";
  label: string;
} => {
  switch (status) {
    case "paid":
      return { badgeVariant: "default", label: i18n.t("Paid", { ns: "payments" }) };
    case "open":
      return { badgeVariant: "outline", label: i18n.t("Open", { ns: "payments" }) };
    case "draft":
      return { badgeVariant: "secondary", label: i18n.t("Draft", { ns: "payments" }) };
    case "void":
      return { badgeVariant: "secondary", label: i18n.t("Void", { ns: "payments" }) };
    case "uncollectible":
      return { badgeVariant: "destructive", label: i18n.t("Uncollectible", { ns: "payments" }) };
  }
};

export const InvoiceCard = ({ invoice }: InvoiceCardProps) => {
  const { t } = useTranslation("payments");
  const { badgeVariant, label } = getStatusInfo(invoice.status);

  const handleDownload = () => {
    if (!invoice.invoice_pdf) return;
    const a = document.createElement("a");
    a.href = invoice.invoice_pdf;
    a.download = `invoice-${invoice.number}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="px-4">
        <div className="flex gap-3 items-center">
          {/* Invoice icon */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" />
            </div>
          </div>

          {/* Invoice details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-sm truncate">{t("Invoice {{number}}", { number: invoice.number })}</h4>
              <Badge variant={badgeVariant} className="text-xs shrink-0">
                {label}
              </Badge>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
              <span>{formatDate(invoice.created * 1000)}</span>
              <span>•</span>
              <span>{formatCurrency(invoice.amount_due, invoice.currency)}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex-shrink-0 flex gap-1">
            {invoice.hosted_invoice_url && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => window.open(invoice.hosted_invoice_url!, "_blank", "noopener,noreferrer")}
                title={t("View Invoice")}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}

            {invoice.invoice_pdf && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleDownload}
                title={t("Download PDF")}
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
